import { useCallback, useState } from "react"
import { useViewedCallback } from "@/analytics/react"
import { myLeadsTermsViewed } from "@/analytics/events/properties"
import { Linking, TouchableOpacity, View } from "react-native"
import BFText, { BodyBold, H5, Tiny } from "@/components/standard/BFText"
import AuthGmailButton from "@/components/buttons/myleads/AuthGmailButton"

interface ConnectPopoverProps {
  onSuccess: (code: string | null, redirectUri?: string) => void
  onDismiss: () => void
}

export const ConnectPopover: React.FC<ConnectPopoverProps> = ({
  onSuccess,
  onDismiss,
}: ConnectPopoverProps) => {
  const [allowed, setAllowed] = useState<boolean>(false)

  useViewedCallback(() => {
    myLeadsTermsViewed()
  }, [])

  const onYes = useCallback(() => {
    setAllowed(true)
  }, [])

  return (
    <View className="flex flex-col items-center flex-1">
      <BodyBold className="m-8 mb-6 text-white">
        I am a U.S. resident, 18 years or older.
      </BodyBold>
      <View className="flex flex-row w-[240px]">
        <TouchableOpacity
          testID={"yes-button"}
          className={`w-[110px] h-[35px] rounded-[20px] flex items-center justify-center ${allowed ? "bg-teal" : "bg-calm-blue border-2 border-white"}`}
          onPress={onYes}
        >
          <H5
            className={`text-center ${allowed ? "text-calm-blue" : "text-white leading-[30px]"}`}
          >
            Yes
          </H5>
        </TouchableOpacity>
        <TouchableOpacity
          className="w-[110px] h-[35px] rounded-[20px] flex items-center justify-center bg-calm-blue border-2 border-white ml-8"
          onPress={onDismiss}
          testID={"no-button"}
        >
          <H5 className="text-center text-white">No</H5>
        </TouchableOpacity>
      </View>
      <View className="w-[180px] h-px bg-white mt-8 mb-6" />
      <View className="flex flex-col flex-1 px-10">
        <Tiny className="text-white">
          Backflip wants to access your Google Account to automatically gather
          properties from your Gmail inbox and pull them into our app for you to
          view and analyze.
        </Tiny>
        <Tiny className="text-white mt-6">
          Backflip&apos;s use and transfer of information received from Google
          API will adhere to&nbsp;
          <BFText
            className="text-teal font-spacegrotesk-bold"
            onPress={() => {
              void Linking.openURL(
                "https://developers.google.com/terms/api-services-user-data-policy"
              )
            }}
          >
            Google API Services User Data Policy
          </BFText>
          , including the&nbsp;
          <BFText
            className="text-teal font-spacegrotesk-bold"
            onPress={() => {
              void Linking.openURL(
                "https://developers.google.com/terms/api-services-user-data-policy/#additional_requirements_for_specific_api_scopes"
              )
            }}
          >
            Limited Use Requirements
          </BFText>
          . Backflip cannot share, edit, download or delete your Gmail emails,
          including any information on properties from your emails, or contacts.
        </Tiny>
        <Tiny className="text-white mt-6">
          See our&nbsp;
          <BFText
            className="text-teal font-spacegrotesk-bold"
            onPress={() => {
              void Linking.openURL("https://dobackflip.com/privacy/")
            }}
          >
            Privacy Policy
          </BFText>
          &nbsp;and&nbsp;
          <BFText
            className="text-teal font-spacegrotesk-bold"
            onPress={() => {
              void Linking.openURL(
                "https://dobackflip.com/backflip-terms-of-use-agreement/"
              )
            }}
          >
            Terms of Use
          </BFText>
          &nbsp;to learn how Backflip safeguards data.
        </Tiny>
      </View>
      <AuthGmailButton
        onSuccess={onSuccess}
        onDismiss={onDismiss}
        disabled={!allowed}
      />
    </View>
  )
}
